'use client'

import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react'
import { getAssetKey } from '@/lib/assets/assets'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import client from '@/lib/api/client'
import { isAudioMainCategory, MainCategory } from '@/lib/categories/categories'
import styles from '@/components/assets-explorer/assets-explorer.module.scss'
import Link from 'next/link'
import { cn, getSearchLink } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { AssetPreview } from '@/components/assets-explorer/asset-preview'
import { Button, buttonVariants } from '@/components/ui/button'
import AssetsGridSkeleton from '@/components/assets-explorer/assets-grid-skeleton'
import { AssetPopup } from '@/components/assets-explorer/asset-popup'
import { XIcon } from 'lucide-react'
import useAssetsPopupNavigationControls from '@/lib/hooks/useAssetsPopupNavigationControls'
import MobileFiltersPanel from '@/components/assets-explorer/filters/mobile-filters-panel'
import DesktopFiltersSidebar from '@/components/assets-explorer/filters/desktop-filters-sidebar'
import useCountGridColumns from '@/lib/hooks/useCountGridColumns'
import { AssetSummary, GetAssetsResponse } from '@/lib/assets/types'
import { useFiltersStore } from '@/lib/store/filters-store'
import { useShallow } from 'zustand/react/shallow'
import { MeilisearchAssetRepository } from '@/lib/assets/repositories/meilisearch-asset-repository'
import { useSearchStore } from '@/lib/store/search-store'
import useSyncAvailableKeywords from '@/lib/hooks/useSyncAvailableKeywords'
import AssetsVirtualGrid from '@/components/assets-explorer/assets-virtual-grid'
import { MusicBar } from '@/components/ui/music-bar'
import { defaultSearchSortFilterValue } from '@/lib/filters/filters'
import { NoResultsState } from '@/components/assets-explorer/no-results-state'
import AudioLayoutToggle from '@/components/assets-explorer/audio-layout-toggle'
import useAudioAssetsLayout from '@/lib/hooks/use-audio-assets-layout'

type GroupedAssets = {
  mainCategory: MainCategory
  assets: AssetSummary[]
  totalAssets: number
}

const currentLocaleFormatter = new Intl.NumberFormat()

const SearchResultItem = ({
  asset,
  isAudio,
  setSelectedAsset,
}: {
  asset: AssetSummary
  isAudio: boolean
  setSelectedAsset: Dispatch<AssetSummary>
}) => {
  return (
    <li className="tw-relative tw-z-[0] tw-duration-500 tw-animate-in tw-fade-in">
      {isAudio && <MusicBar asset={asset} />}
      {!isAudio && (
        <AssetPreview
          asset={asset}
          onClick={() => {
            setSelectedAsset(asset)
          }}
        />
      )}
    </li>
  )
}

/**
 * Search results preview.
 */
const SearchResultsPreview = ({
  query,
  isAudio,
  group,
  setSelectedAsset,
}: {
  query?: string
  isAudio: boolean
  group: GroupedAssets
  setSelectedAsset: Dispatch<AssetSummary>
}) => {
  const gridRef = React.useRef<HTMLUListElement>(null)

  const { count } = useCountGridColumns({
    target: gridRef,
    minWidth: 260,
    gap: 8,
  })

  // Calculate how may columns to show
  const visibleAssets = useMemo(() => {
    if (!count) {
      return 0
    }

    if (isAudio) {
      return 5
    }

    return Math.max(1, count - 1)
  }, [count, isAudio])

  // Memoize the assets to show
  const assets = useMemo(() => group.assets.slice(0, visibleAssets), [group.assets, visibleAssets])

  return (
    <ul
      ref={gridRef}
      className={cn({
        [styles.assetGallery]: !isAudio,
        [styles.audioList]: isAudio,
      })}
    >
      {assets.map((asset, index) => (
        <SearchResultItem
          key={`search-result-item-${asset.name}-${index}`}
          asset={asset}
          isAudio={isAudio}
          setSelectedAsset={setSelectedAsset}
        />
      ))}

      {isAudio && (
        <li className="tw-mt-4 tw-flex tw-justify-center">
          <Link href={getSearchLink(query ?? '', group.mainCategory.slug)}>
            <Button variant="outline">
              View all &quot;{query}&quot; {group.mainCategory.name} assets
            </Button>
          </Link>
        </li>
      )}

      {!isAudio && (
        <li className="tw-flex tw-place-items-center tw-items-stretch">
          <Link
            className="tw-w-full"
            href={`/search/${encodeURIComponent(query ?? '')}?main_category=${group.mainCategory.slug}`}
          >
            <Button variant="outline" className="!tw-h-full !tw-w-full">
              View all &quot;{query}&quot; {group.mainCategory.name} assets
            </Button>
          </Link>
        </li>
      )}
    </ul>
  )
}

export default function SearchResults() {
  const query = useSearchStore((state) => state.search)
  const searchMainCategorySlug = useSearchStore((state) => state.searchMainCategorySlug)

  // Resolve the current effective mainCategorySlug.
  // If the main category is "all", set it to undefined.
  const mainCategorySlug = useMemo(() => {
    if (searchMainCategorySlug === 'all') {
      return undefined
    }
    return searchMainCategorySlug
  }, [searchMainCategorySlug])

  const [selectedAsset, setSelectedAsset] = useState<AssetSummary | null>(null)

  const selectedAssetKey = useMemo(() => {
    return selectedAsset ? getAssetKey(selectedAsset) : ''
  }, [selectedAsset])

  const isMultiCategorySearch = !mainCategorySlug

  // Globally applied filters
  const filters = useFiltersStore((state) => state.filters)

  // Handle query special cases
  // "new" is a special case that should not be used as a search term but a sorting option
  const isNewQuery = useMemo(() => {
    const sortFilter = filters.find((filter) => filter.__typename === 'SortFilter')

    if (!sortFilter) {
      return false
    }

    return !sortFilter.enabled && sortFilter.value === 'new'
  }, [filters])

  // "free" is a special case that should not be used as a search term but a filtering option
  const isFreeQuery = useMemo(() => {
    const premiumFilter = filters.find((filter) => filter.__typename === 'PremiumFilter')

    if (!premiumFilter) {
      return false
    }

    return !premiumFilter.enabled && premiumFilter.value === false
  }, [filters])

  const isSpecialQuery = isNewQuery || isFreeQuery

  const premium = useMemo(() => {
    return filters.find((filter) => filter.__typename === 'PremiumFilter')?.value
  }, [filters])

  const baseSearchLink = useMemo(() => {
    return getSearchLink('', mainCategorySlug ?? 'all')
  }, [mainCategorySlug])

  const newestAssetsLink = useMemo(() => {
    return `${baseSearchLink}${baseSearchLink.includes('?') ? '&' : '?'}sort=new`
  }, [baseSearchLink])

  /*
  const sort = useMemo(() => {
    return filters.find((filter) => filter.__typename === 'SortFilter')?.value
  }, [filters])
  */
  const sort = useMemo(() => {
    return filters.find((filter) => filter.__typename === 'SortFilter')?.value ?? defaultSearchSortFilterValue
  }, [filters])

  const { data: mainCategories, isLoading: isLoadingMainCategories } = useQuery({
    queryKey: ['mainCategories'],
    queryFn: () => client.get<MainCategory[]>('/main-categories').then(({ data }) => data),
  })

  const mainCategory = useMemo(
    () => mainCategories?.find((mc) => mc.slug === mainCategorySlug),
    [mainCategories, mainCategorySlug]
  )

  const mainCategoryIsAudio = useMemo(() => {
    if (!mainCategory) {
      return false
    }

    return isAudioMainCategory(mainCategory.slug)
  }, [mainCategory])
  const { isMobileViewport, audioAssetsLayoutPreference, resolvedAudioGridLayout, setAudioAssetsLayoutPreference } =
    useAudioAssetsLayout(mainCategoryIsAudio)

  // When in multiple category mode, search the first page of each main category
  const { data: multiCategoryAssetsResults, isLoading: isLoadingMultiCategoryAssets } = useQuery<GroupedAssets[]>({
    queryKey: ['search', 'multi-category', { query, premium, sort }],
    queryFn: async () => {
      const calls = mainCategories?.map(async (mainCategory) => {
        const assetRepository = new MeilisearchAssetRepository()
        let assetsResponse: GetAssetsResponse | null = null

        if (isSpecialQuery) {
          assetsResponse = await assetRepository.getAssets({
            mainCategorySlug: mainCategory.slug,
            premium: premium ?? (isFreeQuery ? false : undefined),
            sort: sort ?? (isNewQuery ? 'new' : undefined),
          })
        } else {
          assetsResponse = await assetRepository.getAssets({
            mainCategorySlug: mainCategory.slug,
            search: query,
            premium,
            sort,
          })
        }
        return assetsResponse
      })

      const results = await Promise.all(calls ?? [])

      return results.reduce((acc, response, index) => {
        const mainCategory = mainCategories?.[index]
        const assets = response.data
        const totalAssets = response.meta?.total_items ?? 0

        if (!!mainCategory && response.data.length > 0) {
          acc.push({
            mainCategory,
            assets,
            totalAssets,
          })
        }

        return acc
      }, [] as GroupedAssets[])
    },
    enabled: isMultiCategorySearch && !isLoadingMainCategories,
  })

  // When in single category mode, use infinite query to load more assets

  const usedKeywords = useFiltersStore(useShallow((state) => state.usedKeywords))
  const clearRefinement = useFiltersStore((state) => state.clearRefinement)

  // Get the assets based on the selected categories and filters
  // Also take into account the main searchQuery query, tags and other filters
  const tagsQueryKey = useMemo(() => {
    return usedKeywords.sort().join(',')
  }, [usedKeywords])

  const queryKey = useMemo(() => {
    return [
      'search',
      {
        query,
        mainCategorySlug,
        premium,
        sort,
        tagsQueryKey,
      },
    ]
  }, [mainCategorySlug, premium, query, sort, tagsQueryKey])

  const {
    data: singleCategoryAssetsResults,
    isLoading: isLoadingSingleCategoryAssets,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      console.debug(`Loading assets for: "${query}", main category: ${mainCategorySlug}, page: ${pageParam}`)

      const assetRepository = new MeilisearchAssetRepository()
      let assetsResponse: GetAssetsResponse | null = null

      if (isSpecialQuery) {
        assetsResponse = await assetRepository.getAssets({
          mainCategorySlug,
          premium: premium ?? (isFreeQuery ? false : undefined),
          sort: sort ?? (isNewQuery ? 'new' : undefined),
          tags: usedKeywords,
          page: pageParam,
          signal,
        })
      } else {
        assetsResponse = await assetRepository.getAssets({
          mainCategorySlug,
          search: query,
          premium,
          sort,
          tags: usedKeywords,
          page: pageParam,
          signal,
        })
      }

      return assetsResponse
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // If the last page has no next cursor, return undefined
      if (!lastPage.meta) {
        return undefined
      }

      if (lastPage.meta.total_items === 0) {
        return undefined
      }

      // If the last page is the last page, return undefined
      if (lastPage.meta.current_page >= lastPage.meta.total_pages) {
        return undefined
      }

      // Otherwise, return the next page
      return lastPage.meta.current_page + 1
    },
    getPreviousPageParam: (firstPage) => {
      // If the first page has no previous cursor, return undefined
      if (!firstPage.meta) {
        return undefined
      }

      // If the first page is the first page, return undefined
      if (firstPage.meta.current_page <= 1) {
        return undefined
      }

      // Otherwise, return the previous page
      return firstPage.meta.current_page - 1
    },
    enabled: !isMultiCategorySearch && !isLoadingMainCategories,
  })

  // Clear local refinements when the component is unmounted
  useEffect(() => {
    return () => {
      clearRefinement()
    }
  }, [clearRefinement])

  const isLoadingAssets = isLoadingSingleCategoryAssets || isLoadingMultiCategoryAssets || isLoadingMainCategories

  // Show the filters sidebar when a main category is selected
  const showFilterSidebar = true // !!mainCategorySlug

  // Assets popup controls
  const {
    hasPreviousAsset,
    hasNextAsset,
    navigateToPreviousAsset,
    navigateToNextAsset,
    isLoading: isLoadingNavigationControls,
  } = useAssetsPopupNavigationControls({
    data: singleCategoryAssetsResults,
    selectedAsset,
    setSelectedAsset,
    fetchNextPage,
  })

  // Calculate total assets based on search mode
  const totalAssets = useMemo(() => {
    if (isMultiCategorySearch) {
      return (
        multiCategoryAssetsResults?.reduce((acc, group) => {
          return acc + group.totalAssets
        }, 0) ?? 0
      )
    }

    return singleCategoryAssetsResults?.pages[0].meta?.total_items ?? 0
  }, [singleCategoryAssetsResults, multiCategoryAssetsResults, isMultiCategorySearch])

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // Keep the available keywords in sync
  useSyncAvailableKeywords({
    enabled: !isMultiCategorySearch,
    mainCategorySlug,
    search: query,
    premium,
  })

  // But when the query is empty, clear the keywords
  const setAvailableKeywords = useFiltersStore((state) => state.setAvailableKeywords)

  useEffect(() => {
    if (!query) {
      setAvailableKeywords([])
    }
  }, [query, setAvailableKeywords])

  const lastReportSigRef = React.useRef<string>('')

  const reportSearchResults = useCallback(() => {
    const payload = {
      query: query ?? '',
      mainCategorySlug: mainCategorySlug ?? 'all',
      showFreeFilter: typeof premium !== 'undefined' && premium === false ? true : false,
      sort,
      tagsQueryKey,
      usedKeywords,
      totalAssets,
      isMultiCategorySearch,
    }

    const sig = JSON.stringify(payload)
    if (sig === lastReportSigRef.current) return
    lastReportSigRef.current = sig

    try {
      // SSR-safe
      const session = typeof window !== 'undefined' && (window as any).getGlobalSession?.()

      session?.analytics?.sendCustomEvent?.('search_results', payload)
    } catch (err) {
      console.debug('analytics sendCustomEvent error', err)
    }
  }, [query, mainCategorySlug, premium, sort, tagsQueryKey, usedKeywords, totalAssets, isMultiCategorySearch])

  useEffect(() => {
    if (!isLoadingAssets && (query || isSpecialQuery)) {
      reportSearchResults()
    }
  }, [isLoadingAssets, isSpecialQuery, query, reportSearchResults])

  const showMobileFiltersPanel = showFilterSidebar && isMobileViewport

  return (
    <>
      <div
        className={cn('tw-grid tw-h-full tw-grid-rows-[auto_1fr] tw-gap-2', {
          'tw-grid-cols-[1fr] md:tw-grid-cols-[auto_1fr]': showFilterSidebar,
          'tw-grid-cols-[1fr]': !showFilterSidebar,
        })}
      >
        {showFilterSidebar && (
          <DesktopFiltersSidebar
            stickyTop="var(--search-results-sidebar-sticky-top)"
            stickyClassName="tw-pr-0 tw-pt-2"
            refinementEnabled={{ search: false }}
          />
        )}
        <div className="tw-sticky tw-top-[var(--header-height)] tw-z-[1] tw-bg-[rgb(var(--background-start-rgb))]">
          <nav
            className={`tw-flex tw-max-w-full tw-flex-row tw-gap-2 tw-overflow-x-auto tw-border-red-500 md:tw-max-w-none md:tw-overflow-visible md:tw-border-0`}
          >
            {isLoadingMainCategories && (
              <div className="tw-flex tw-w-full tw-justify-center tw-space-x-2 tw-p-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="tw-h-6 tw-w-16" />
                ))}
              </div>
            )}

            {!isLoadingMainCategories && (
              <ul className="tw-flex tw-w-auto tw-justify-center">
                {/*<li className="tw-mr-2 tw-self-center">*/}
                {/*  <Link*/}
                {/*    href={`/search/new`}*/}
                {/*    className={cn(buttonVariants({ variant: 'outline' }), {*/}
                {/*      '!tw-bg-[var(--Secondary)] !tw-text-secondary-foreground !tw-outline-none':*/}
                {/*        isNewQuery && !mainCategorySlug,*/}
                {/*    })}*/}
                {/*  >*/}
                {/*    New*/}
                {/*  </Link>*/}
                {/*</li>*/}
                <li className={cn(styles.navItem, '')}>
                  <Link
                    href={getSearchLink(query ?? '', 'all')}
                    className={cn({
                      [styles.active]: !mainCategorySlug,
                    })}
                  >
                    ALL
                  </Link>
                </li>
                {mainCategories?.map((mainCategory) => (
                  <li
                    key={`main-category-nav-item-${mainCategory.slug}`}
                    className={cn(styles.navItem, 'tw-w-max md:tw-w-auto')}
                  >
                    <Link
                      href={getSearchLink(query ?? '', mainCategory.slug)}
                      className={cn({
                        [styles.active]: mainCategory.slug === mainCategorySlug,
                      })}
                    >
                      {mainCategory.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="tw-ml-auto tw-hidden tw-w-auto tw-items-center tw-justify-end tw-gap-2 tw-pr-2 md:tw-flex">
              {isLoadingAssets && !isSpecialQuery && <Skeleton className="tw-m-2 tw-h-10 tw-w-96 tw-max-w-full" />}

              {!isLoadingAssets && (
                <>
                  {!isSpecialQuery && (
                    <div className="body_2 tw-p-2 tw-text-center tw-italic tw-text-gray-600">
                      {totalAssets > 0 ? currentLocaleFormatter.format(totalAssets) : 'No'} results
                      {/* hide these details for the moment*/}
                      {false && (
                        <>
                          {!!query && <span> for &quot;{query ?? ''}&quot;</span>}{' '}
                          {!!mainCategory && (
                            <>
                              in{' '}
                              <Link
                                href={`/${mainCategory?.slug}`}
                                className="tw-text-(var(--Primary)) tw-underline hover:tw-text-[var(--Secondary)]"
                              >
                                {mainCategory?.name}
                              </Link>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {mainCategoryIsAudio && !isMultiCategorySearch && !isMobileViewport && (
                    <AudioLayoutToggle
                      value={audioAssetsLayoutPreference}
                      onChangeAction={setAudioAssetsLayoutPreference}
                    />
                  )}
                  {/* Clear results pill -- Hiden for the moment */}
                  {!!query && false && (
                    <a
                      href={getSearchLink('', mainCategory?.slug)}
                      className={buttonVariants({ variant: 'outline', shape: 'rounded', size: 'sm' })}
                    >
                      <XIcon className="tw-mr-2 tw-h-4 tw-w-4" />
                      Clear results
                    </a>
                  )}
                </>
              )}
            </div>
          </nav>
          {showMobileFiltersPanel && <MobileFiltersPanel refinementEnabled={{ search: false }} />}
        </div>
        <div className="tw-mt-2 tw-grid tw-w-full tw-grid-rows-[min-content_1fr] tw-gap-4 tw-overflow-hidden tw-pr-2">
          {/*Assets*/}
          {isLoadingAssets && (
            <div className="tw-grid tw-gap-4">
              {isMultiCategorySearch && <Skeleton className="tw-h-6 tw-w-16" />}
              <AssetsGridSkeleton isAudio={mainCategoryIsAudio} />
            </div>
          )}

          {!isLoadingAssets && (
            <div className="tw-flex tw-flex-col tw-space-y-8">
              {/* Multi category search results */}
              {isMultiCategorySearch && multiCategoryAssetsResults && (
                <>
                  {multiCategoryAssetsResults.map((group) => {
                    const isAudio = isAudioMainCategory(group.mainCategory.slug)

                    return (
                      <div
                        key={`main-category-${group.mainCategory.slug}`}
                        className="tw-flex tw-flex-col tw-space-y-4 tw-px-2"
                      >
                        <h2 className="subheading_2">{group.mainCategory.name}</h2>

                        <SearchResultsPreview
                          query={query}
                          isAudio={isAudio}
                          group={group}
                          setSelectedAsset={setSelectedAsset}
                        />
                      </div>
                    )
                  })}
                </>
              )}

              {!isMultiCategorySearch && !!singleCategoryAssetsResults && (
                <AssetsVirtualGrid
                  isAudio={mainCategoryIsAudio}
                  audioGridLayout={resolvedAudioGridLayout}
                  assetsPages={singleCategoryAssetsResults?.pages ?? []}
                  loadMore={loadMore}
                  setSelectedAsset={setSelectedAsset}
                />
              )}

              {!totalAssets && (
                <NoResultsState
                  query={query}
                  primaryAction={{
                    label: 'Reset search',
                    href: baseSearchLink,
                  }}
                  secondaryAction={{
                    label: 'Jump to newest drops',
                    href: newestAssetsLink,
                    variant: 'ghost-stroke',
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {!!selectedAsset && !!selectedAsset.mainCategorySlug && (
        <AssetPopup
          assetId={selectedAssetKey}
          mainCategoryId={selectedAsset.mainCategorySlug}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedAsset(null)
            }
          }}
          hasNext={hasNextAsset}
          hasPrev={hasPreviousAsset}
          onPrev={navigateToPreviousAsset}
          onNext={navigateToNextAsset}
          disableNavigation={isLoadingNavigationControls}
        />
      )}
    </>
  )
}
